<template>
  <div class="grid grid-cols-5 col-span-5">
    <div v-if="message.newRequestLegend && messageIndex !== 0"
         class="col-span-5 h-0 mt-8 ml-4 border-solid border-0 border-t border-soft"/>
    <div v-if="message.newRequestLegend"
         :class="`inline-flex col-span-5 mt-${messageIndex === 0 ? '5' : '8'} pl-4 border-solid border-0 border-l-2 border-${message.borderColor}`">
      <chart-legend :type="message.newRequestLegend" :show-label="false"/>
      <span class="ml-3 text-gray-dark text-sm">{{ formatTimestamp(message.sent || message.timestamp) }}</span>
      <copyable-link v-if="getBrowserUrl(message)" :url="getBrowserUrl(message)" class="ml-3"/>
    </div>

    <vx-tooltip :text="formatTimestamp(message.sent || message.timestamp)"
                :class="`col-span-3 pt-4 pl-4 border-solid border-0 border-l-2 border-${message.borderColor}`"
                position="left"
                delay="0.7s"
    >
      <div v-if="messageEmailSubject" class="flex items-center text-sm">
        <div class="text-gray">Subject:</div>
        <div class="text-gray-dark ml-1 truncate">{{ messageEmailSubject }}</div>
      </div>

      <customer-bubble
        :class="{'mt-3': !message.newRequestLegend, 'cursor-pointer': getAnswer(message) || fileUrl}"
        :customerInput="isFileMessage ? 'Uploaded file' : message.data.content.payload"
        :focus="isCustomerBubbleFocused(message.data.message_id)"
        :is-clickable="!!getAnswer(message) || !!fileUrl"
        @click.native="selectMessage(message)">
        <template #footer>
          <div v-if="getAnswer(message)" class="flex items-center mt-2 text-sm text-primary">
            <answers-icon :is-filled="true" class="h-3"/>
            <router-link :key="message.data.message_id"
                         :to="`/bots/${botId}/answers/${getAnswer(message).id}`"
                         target="_blank"
                         class="hover:underline"
            >
              {{ formatIntent(getAnswerName(message)) }}
            </router-link>
          </div>
          <a :href="fileUrl"
             v-if="fileUrl"
             target="_blank"
             rel="noopener noreferrer"
             class="text-primary hover:underline break-all"
             @click.stop
          >{{ fileUrl }}</a>
        </template>
      </customer-bubble>
    </vx-tooltip>
  </div>
</template>

<script>
import CustomerBubble from '../../../preview/v2/CustomerBubble.vue'
import ChartLegend from '@/components/analytics/charts/ChartLegend.vue'
import CopyableLink from '@/components/analytics/conversations/detail/CopyableLink.vue'
import AnswersIcon from '@/assets/images/icons/AnswersIcon.vue'
import datetimeUtils from '@/utils/datetime.utils'
import conversationDetailModule from '@/store/modules/conversation-detail.module'

export default {
  name: 'ConversationClientMessage',

  components: {
    AnswersIcon,
    ChartLegend,
    CopyableLink,
    CustomerBubble
  },

  props: {
    message: {
      type: Object,
      required: true
    },
    messageIndex: {
      type: Number,
      required: true
    },
    botId: {
      type: String,
      default: null
    },
    answersByIntentId: {
      type: Object,
      default: () => ({})
    },
    selectedBubble: {
      type: Object,
      default: null
    }
  },

  computed: {
    requests: conversationDetailModule.getRequests,

    messageId() {
      return this.message.data.message_id
    },

    messageEmailSubject() {
      return this.message.data.metadata?.subject
    },

    isFileMessage() {
      return this.message.data.content?.type === 'file'
    },

    fileUrl() {
      if (!this.isFileMessage) return null
      const url = this.message.data.content.payload?.url
      return typeof url === 'string' && /^https?:\/\//i.test(url) ? url : null
    }
  },

  methods: {
    formatTimestamp(timestamp) {
      return datetimeUtils.toMoment(timestamp).format('LL HH:mm:ss')
    },

    getBrowserUrl(message) {
      return message.data.metadata?.browserUrl
    },

    getAnswer(message) {
      const intentPlatformId = this.requests[message?.data?.message_id]?.intent
      return intentPlatformId ? this.answersByIntentId[intentPlatformId] : null
    },

    getAnswerName(message) {
      return this.getAnswer(message)?.name || 'Unknown answer'
    },

    formatIntent(intent) {
      return intent.replaceAll('_', '_<wbr>')
    },

    isCustomerBubbleFocused(messageId) {
      return this.selectedBubble ? this.selectedBubble.messageId === messageId : false
    },

    selectMessage(message) {
      this.$emit('bubble-selected', message)
    }
  }
}
</script>
